import { useNavigation, useSearchParams } from 'react-router';
import { updateURLSearchParamsWithProjectContext } from '@gonfalon/app-context/updateURLSearchParamsWithProjectContext';
import { useProjectContext } from '@gonfalon/app-context/useProjectContext';
import { useDebounce } from '@gonfalon/async/useDebounce';
import type { Statement } from '@gonfalon/audit-log/types';
import { useRouteActivitySpec } from '@gonfalon/audit-log/useRouteActivitySpec';
import { enableChangeHistory, enableMostRecentAuditLogHistory } from '@gonfalon/dogfood-flags/dogfood-flags';
import type { ResourceSpecifier } from '@gonfalon/resource-specifiers/ast';
import { isEnvironmentScopedResource } from '@gonfalon/resource-specifiers/isEnvironmentScopedResource';
import { parse as resourceSpecifierFromString } from '@gonfalon/resource-specifiers/parser';
import { mergeSearchParams } from '@gonfalon/router/mergeSearchParams';
import { trackError } from '@gonfalon/telemetry/trackError';
import { useQueryClient } from '@tanstack/react-query';
import invariant from 'tiny-invariant';
// oxlint-disable-next-line no-restricted-imports -- TODO(zod-4): migrate to 'zod/v4'
import { z } from 'zod';

import { changeHistoryTracker, endChangeHistoryExperienceIfActive } from './analytics';
import {
  addResource,
  anyResource,
  type ActivityDateFilter,
  CHANGE_HISTORY_QUERY_PARAMS,
  deserializeActivityFilter,
  serializeActivityFilter,
} from './ChangeHistory/ChangeHistoryUtils';
import { getFilterDateRange } from './getFilterDateRange';
import { invalidateDependentQueries } from './internal/invalidateDependentQueries';

const statement = z.object({
  actions: z.array(z.string()).optional(),
  notActions: z.array(z.string()).optional(),
  effect: z.enum(['allow', 'deny']),
  resources: z.array(z.string()).optional(),
  notResources: z.array(z.string()).optional(),
});

export const useChangeHistory = () => {
  const projectContext = useProjectContext({ optional: true });
  const navigation = useNavigation();
  const [searchParams, setSearchParams] = useSearchParams();
  const routeSpec = useRouteActivitySpec();
  const queryClient = useQueryClient();

  const urlFilters = deserializeActivityFilter(searchParams);
  let filters = urlFilters;
  if (!enableMostRecentAuditLogHistory() && (!filters.after || !filters.before)) {
    const { start, end } = getFilterDateRange(filters.before, filters.after);
    filters = { ...filters, after: start, before: end };
  }
  const isChangeHistoryVisible =
    navigation.state === 'loading' && navigation.location
      ? new URLSearchParams(navigation.location.search).has(CHANGE_HISTORY_QUERY_PARAMS.ACTIVITY)
      : searchParams.has(CHANGE_HISTORY_QUERY_PARAMS.ACTIVITY);

  const optimisticSearchParams = navigation.location
    ? new URLSearchParams(navigation.location.search)
    : new URLSearchParams();
  const specParam = optimisticSearchParams.getAll(CHANGE_HISTORY_QUERY_PARAMS.SPEC);

  let spec =
    navigation.state === 'loading' && navigation.location
      ? specParam
          .map((s) => {
            try {
              return JSON.parse(decodeURIComponent(s));
            } catch {
              return '';
            }
          })
          .filter(Boolean)
      : filters.spec;
  let parsedResource: ResourceSpecifier | undefined;

  spec = spec ?? routeSpec;

  try {
    const parsedSpec = statement.array().safeParse(spec);
    if (parsedSpec.success && parsedSpec.data.length > 0) {
      const [firstResource] = parsedSpec.data[0].resources ?? [];

      if (firstResource) {
        const parsedResourceSpecifier = resourceSpecifierFromString(firstResource);
        if (parsedResourceSpecifier.ok) {
          parsedResource = parsedResourceSpecifier.val;
        }
      }
    }
  } catch (error) {
    if (error instanceof Error) {
      trackError(error, { severity: 'medium' });
    }
  }

  const open = (props?: {
    policy?: Statement[];
    after?: Date;
    before?: Date;
    sort?: 'asc' | 'desc';
    auditId?: string;
  }) => {
    if (!enableChangeHistory()) {
      return;
    }

    const startAttributes: {
      resourceType?: string;
      isList?: boolean;
      openAuditDetail?: boolean;
    } = {};
    if (parsedResource) {
      startAttributes.resourceType = parsedResource.type;
      if ('name' in parsedResource) {
        startAttributes.isList = parsedResource.name === anyResource;
      }
    }
    if (props?.auditId) {
      startAttributes.openAuditDetail = true;
    }
    changeHistoryTracker.start(startAttributes);

    let dateFilter: ActivityDateFilter = { mode: 'mostRecent' };
    if (!enableMostRecentAuditLogHistory() || props?.before || props?.after) {
      const { start, end } = getFilterDateRange(props?.before, props?.after);
      dateFilter = { mode: 'range', after: start, before: end };
    }
    const filtersFromSpecParams = serializeActivityFilter(
      {
        spec: props?.policy ?? routeSpec,
        dateFilter,
        sort: props?.sort,
      },
      searchParams,
    );

    const mergedParams = mergeSearchParams(searchParams, filtersFromSpecParams);

    if (props?.auditId) {
      mergedParams.set(CHANGE_HISTORY_QUERY_PARAMS.ACTIVITY_AUDIT_ID, props.auditId);
      // Mark as detail-only view (no list context to return to)
      mergedParams.set(CHANGE_HISTORY_QUERY_PARAMS.ACTIVITY_DETAIL_ONLY, 'true');
    }

    setSearchParams(mergedParams);
  };

  const close = (nextParams?: URLSearchParams) => {
    endChangeHistoryExperienceIfActive('cancel');
    const next = nextParams ? new URLSearchParams([...nextParams, ...searchParams]) : new URLSearchParams(searchParams);
    Object.values(CHANGE_HISTORY_QUERY_PARAMS).forEach((key) => {
      next.delete(key);
    });
    setSearchParams(next);

    const resourceKeys = resourceKeysFromParsedResource(parsedResource);
    if (resourceKeys) {
      void invalidateDependentQueries(queryClient, resourceKeys);
    }
  };

  const deleteResourceFilter = (index: number): void => {
    invariant(spec, 'spec should be defined');

    changeHistoryTracker.track('Delete resource filter');

    const newSpec = [...spec];
    newSpec.splice(index, 1);

    const nextFilter = serializeActivityFilter(
      {
        spec: newSpec,
      },
      searchParams,
    );

    if (projectContext) {
      const nextParams = updateURLSearchParamsWithProjectContext(nextFilter, projectContext.context);
      setSearchParams(nextParams);
    } else {
      setSearchParams(nextFilter);
    }
  };

  const addResourceFilter = (resource: ResourceSpecifier['type']): void => {
    invariant(spec, 'spec should be defined');

    changeHistoryTracker.track('Add resource', { resourceType: resource });

    const nextStatement = addResource(
      resource,
      projectContext?.project?.key || '*',
      projectContext?.selectedEnvironment?.key ? [projectContext.selectedEnvironment.key] : ['*'],
    );
    const newSpec = [...spec];
    newSpec.push(nextStatement);

    const nextFilter = serializeActivityFilter(
      {
        spec: newSpec,
      },
      searchParams,
    );

    if (projectContext) {
      const nextParams = updateURLSearchParamsWithProjectContext(nextFilter, projectContext.context);
      setSearchParams(nextParams);
    } else {
      setSearchParams(nextFilter);
    }
  };

  const auditLogIdParam = optimisticSearchParams.get(CHANGE_HISTORY_QUERY_PARAMS.ACTIVITY_AUDIT_ID);
  const auditId =
    navigation.state === 'loading' && auditLogIdParam
      ? auditLogIdParam
      : searchParams.get(CHANGE_HISTORY_QUERY_PARAMS.ACTIVITY_AUDIT_ID);

  const updateResourceFilter = (index: number, nextStatement: Statement) => {
    invariant(spec, 'spec should be defined');

    const newSpec = [...spec];

    newSpec[index] = nextStatement;

    const nextFilter = serializeActivityFilter(
      {
        spec: newSpec,
      },
      searchParams,
    );

    if (projectContext) {
      const nextParams = updateURLSearchParamsWithProjectContext(nextFilter, projectContext.context);
      setSearchParams(nextParams);
    } else {
      setSearchParams(nextFilter);
    }
  };

  const resetResourceFilter = (index: number, resource: ResourceSpecifier['type']) => {
    const nextStatement = addResource(
      resource,
      projectContext?.project?.key || '*',
      projectContext?.selectedEnvironment?.key ? [projectContext.selectedEnvironment.key] : ['*'],
    );
    updateResourceFilter(index, nextStatement);
  };

  const updateTextFilter_ = (text: string) => {
    changeHistoryTracker.track('Update search filter', { hasValue: text.length > 0 });

    const nextFilter = serializeActivityFilter(
      {
        ...urlFilters,
        spec,
        q: text,
      },
      searchParams,
    );

    if (projectContext) {
      const nextParams = updateURLSearchParamsWithProjectContext(nextFilter, projectContext.context);
      setSearchParams(nextParams);
    } else {
      setSearchParams(nextFilter);
    }
  };

  const updateTextFilter = useDebounce(updateTextFilter_, 150);

  const updateDateFilter = (dateFilter: ActivityDateFilter) => {
    changeHistoryTracker.track('Update date filter');

    const nextFilter = serializeActivityFilter(
      {
        ...urlFilters,
        spec,
        dateFilter,
      },
      searchParams,
    );

    if (projectContext) {
      const nextParams = updateURLSearchParamsWithProjectContext(nextFilter, projectContext.context);
      setSearchParams(nextParams);
    } else {
      setSearchParams(nextFilter);
    }
  };

  return {
    hasRouteSpec: routeSpec !== undefined,
    spec,
    filters,
    open,
    close,
    isChangeHistoryVisible,
    addResourceFilter,
    updateResourceFilter,
    deleteResourceFilter,
    resetResourceFilter,
    updateTextFilter,
    updateDateFilter,
    auditId,
    parsedResource,
  };
};

function resourceKeysFromParsedResource(resource: ResourceSpecifier | undefined) {
  if (!resource) {
    return undefined;
  }
  const environment = isEnvironmentScopedResource(resource) ? resource.environment : undefined;
  const projectKey = environment?.project.name ?? '';
  const environmentKey = environment?.name ?? '';
  const resourceKey = resource.type === 'acct' || resource.type === 'ip-allowlist' ? anyResource : resource.name;
  if (
    !projectKey ||
    !environmentKey ||
    !resourceKey ||
    projectKey === anyResource ||
    environmentKey === anyResource ||
    resourceKey === anyResource
  ) {
    return undefined;
  }
  return { projectKey, environmentKey, resourceKey };
}
